import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Undo2 } from 'lucide-react';
import { apiService } from '../../services/api';

// Reformulation par Muse, imposée avec une animation : l'ancien texte s'efface, le nouveau s'écrit lettre à lettre
// dans un reflet violet, puis « Annuler » reste proposé quelques secondes. Pendant que Muse réfléchit, le texte
// d'origine est parcouru d'un reflet (MuseThinking).

const TYPE_MS = 650; // durée de l'écriture, quelle que soit la longueur

export const MuseThinking = ({ children, className = '' }) => (
  <span className={`animate-muse-shimmer bg-gradient-to-r from-current via-indigo-400 to-current bg-[length:200%_100%] bg-clip-text ${className}`}
    style={{ WebkitTextFillColor: 'transparent' }} title="Nexus relit le titre…">
    {children}
  </span>
);

export const MuseRewriteText = ({ from, to, onUndo, onSettled, undoFor = 10000, className = '' }) => {
  const [phase, setPhase] = useState('erase'); // erase → type → done → settled
  const [shown, setShown] = useState(0);
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;

  useEffect(() => {
    const timer = setTimeout(() => setPhase('type'), 380);
    return () => clearTimeout(timer);
  }, []);
  useEffect(() => {
    if (phase !== 'type') return undefined;
    if (shown >= to.length) {
      setPhase('done');
      return undefined;
    }
    const step = Math.max(1, Math.round(to.length / 40));
    const timer = setTimeout(() => setShown((n) => Math.min(to.length, n + step)), TYPE_MS / Math.ceil(to.length / step));
    return () => clearTimeout(timer);
  }, [phase, shown, to]);
  useEffect(() => {
    if (phase !== 'done') return undefined;
    const timer = setTimeout(() => {
      setPhase('settled');
      if (settledRef.current) settledRef.current();
    }, undoFor);
    return () => clearTimeout(timer);
  }, [phase, undoFor]);

  if (phase === 'erase') {
    return <span className={`inline-block animate-muse-erase ${className}`}>{from}</span>;
  }
  return (
    <span className={className}>
      <span className={`rounded transition-colors duration-[1500ms] ${phase === 'type' ? 'bg-indigo-50 text-indigo-700' : phase === 'done' ? 'animate-muse-glow' : ''}`}>
        {phase === 'type' ? to.slice(0, shown) : to}
        {phase === 'type' && <span className="ml-px inline-block h-[1em] w-[2px] translate-y-[2px] animate-pulse bg-indigo-500" />}
      </span>
      {phase === 'done' && onUndo && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onUndo(); }} title={`Revenir à « ${from} »`}
          className="ml-1.5 inline-flex translate-y-[-1px] animate-muse-line-in items-center gap-1 whitespace-nowrap rounded-full bg-indigo-50 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-indigo-600 hover:bg-indigo-100">
          <Sparkles size={10} /> Nexus · <Undo2 size={10} /> Annuler
        </button>
      )}
    </span>
  );
};

// Objectifs fraîchement créés : Muse relit le titre et l'impose s'il le reformule (sauf si on l'a modifié entre-temps) ;
// « Annuler » remet le titre d'origine. `objectivesRef` donne la liste à jour (ref tenue par l'appelant).
export const useObjectiveRewrites = ({ projectId, objectivesRef, setObjectives, onChange }) => {
  const [thinking, setThinking] = useState({});
  const [rewrites, setRewrites] = useState({});
  const rewritesRef = useRef(rewrites);
  rewritesRef.current = rewrites;

  const setName = useCallback((id, name) => {
    setObjectives((prev) => prev.map((o) => (o.id === id ? { ...o, name } : o)));
  }, [setObjectives]);

  const rewrite = useCallback(async (objective) => {
    const original = objective.name;
    if (!projectId || !objective.id || objective.id < 0 || !original) return;
    setThinking((prev) => ({ ...prev, [objective.id]: true }));
    try {
      const data = await apiService.suggestObjectiveTitle(projectId, original);
      const title = data?.suggested_title;
      const current = objectivesRef.current.find((o) => o.id === objective.id);
      if (!title || title === original || !current || current.name !== original) return;
      const updated = await apiService.patch(`/projects/${projectId}/objectives/${objective.id}`, { name: title });
      setName(objective.id, updated?.name || title);
      setRewrites((prev) => ({ ...prev, [objective.id]: { from: original, to: updated?.name || title } }));
      if (onChange) onChange();
    } catch (err) {
      // Muse indisponible : le titre reste celui saisi
    } finally {
      setThinking((prev) => {
        const next = { ...prev };
        delete next[objective.id];
        return next;
      });
    }
  }, [projectId, objectivesRef, setName, onChange]);

  const settle = useCallback((id) => {
    setRewrites((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const undo = useCallback(async (id) => {
    const done = rewritesRef.current[id];
    if (!done) return;
    settle(id);
    setName(id, done.from);
    try {
      await apiService.patch(`/projects/${projectId}/objectives/${id}`, { name: done.from });
      if (onChange) onChange();
    } catch (err) {
      setName(id, done.to);
    }
  }, [projectId, setName, settle, onChange]);

  return { thinking, rewrites, rewrite, undo, settle };
};

export default MuseRewriteText;
